import { useState } from 'react'
import {
  faCircleCheck,
  faCircleQuestion,
  faFlag,
  faTriangleExclamation,
} from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { requestListingBreakdown } from '../../_data/renter-llm'
import { ListingBreakdownResult } from '../../_types/quickpass-llm'
import {
  Section,
  SectionHeader,
  SectionTitle,
  Eyebrow,
  PrimaryAction,
  TextButton,
  PromptBlock,
  OneLineTake,
  ColumnsGrid,
  Column,
  ColumnHeading,
  BulletList,
  Bullet,
  BulletGlyph,
  QuestionsBlock,
  Footer,
  FooterStamp,
  ErrorBanner,
  LoadingShimmer,
} from './styled'
import { BreakdownState, ListingBreakdownProps } from './types'

/**
 * AI-generated breakdown surface on the listing detail page. Calls the
 * `runllmcompletion` factory command on demand, parses the structured
 * response, and renders pros / cons / red flags / questions-to-ask.
 *
 * @param {ListingBreakdownProps} props
 * @returns {JSX.Element}
 */
const ListingBreakdown = ({ listing }: ListingBreakdownProps): JSX.Element => {
  const { profile } = useQuickPassProfile()
  const [state, setState] = useState<BreakdownState>('idle')
  const [result, setResult] = useState<ListingBreakdownResult | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleGenerate = async (): Promise<void> => {
    setState('loading')
    setErrorMessage('')

    try {
      const response = await requestListingBreakdown({
        listing,
        preferences: profile?.unitPreferences ?? null,
        takeHome: profile?.monthlyTakeHomeIncome ?? null,
      })

      if (response === null) {
        setState('offline')
        return
      }

      setResult(response)
      setState('ready')
    } catch (error) {
      setState('error')
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Could not generate the breakdown. Try again in a sec.',
      )
    }
  }

  return (
    <Section>
      <SectionHeader>
        <Eyebrow>QuickPass AI</Eyebrow>
        <SectionTitle>What this listing means for you</SectionTitle>
      </SectionHeader>

      {state === 'idle' ? (
        <>
          <PromptBlock>
            Get a tailored breakdown — pros + cons for your preferences, red
            flags worth watching, and the five most useful questions to ask the
            landlord.
          </PromptBlock>
          <PrimaryAction type="button" onClick={handleGenerate}>
            Generate breakdown
          </PrimaryAction>
        </>
      ) : null}

      {state === 'loading' ? (
        <LoadingShimmer>
          Reading the listing… should take about 5 seconds.
        </LoadingShimmer>
      ) : null}

      {state === 'offline' ? (
        <PromptBlock>
          The AI breakdown needs a configured backend. Sign in with a real
          QuickPass account (or set <code>VITE_API_URL</code>) to see this
          surface light up.
        </PromptBlock>
      ) : null}

      {state === 'error' ? (
        <>
          <ErrorBanner>{errorMessage}</ErrorBanner>
          <PrimaryAction type="button" onClick={handleGenerate}>
            Try again
          </PrimaryAction>
        </>
      ) : null}

      {state === 'ready' && result !== null ? (
        <>
          <OneLineTake>{result.oneLineTake}</OneLineTake>

          <ColumnsGrid>
            <Column>
              <ColumnHeading>
                <BulletGlyph aria-hidden="true">
                  <Icon icon={faCircleCheck} />
                </BulletGlyph>
                Pros
              </ColumnHeading>
              <BulletList>
                {result.pros.map((entry, index) => (
                  <Bullet key={`pros-${index}`}>
                    <BulletGlyph aria-hidden="true">·</BulletGlyph>
                    <span>{entry}</span>
                  </Bullet>
                ))}
              </BulletList>
            </Column>

            <Column>
              <ColumnHeading>
                <BulletGlyph aria-hidden="true">
                  <Icon icon={faTriangleExclamation} />
                </BulletGlyph>
                Cons
              </ColumnHeading>
              <BulletList>
                {result.cons.map((entry, index) => (
                  <Bullet key={`cons-${index}`}>
                    <BulletGlyph aria-hidden="true">·</BulletGlyph>
                    <span>{entry}</span>
                  </Bullet>
                ))}
              </BulletList>
            </Column>
          </ColumnsGrid>

          {result.redFlags.length > 0 ? (
            <Column>
              <ColumnHeading>
                <BulletGlyph aria-hidden="true">
                  <Icon icon={faFlag} />
                </BulletGlyph>
                Red flags
              </ColumnHeading>
              <BulletList>
                {result.redFlags.map((entry, index) => (
                  <Bullet key={`flags-${index}`}>
                    <BulletGlyph aria-hidden="true">·</BulletGlyph>
                    <span>{entry}</span>
                  </Bullet>
                ))}
              </BulletList>
            </Column>
          ) : null}

          <QuestionsBlock>
            <ColumnHeading>
              <BulletGlyph aria-hidden="true">
                <Icon icon={faCircleQuestion} />
              </BulletGlyph>
              Ask the landlord
            </ColumnHeading>
            <BulletList>
              {result.questionsToAsk.map((entry, index) => (
                <Bullet key={`q-${index}`}>
                  <BulletGlyph aria-hidden="true">{index + 1}.</BulletGlyph>
                  <span>{entry}</span>
                </Bullet>
              ))}
            </BulletList>
          </QuestionsBlock>

          <Footer>
            <FooterStamp>
              AI-generated. Verify anything load-bearing.
            </FooterStamp>
            <TextButton type="button" onClick={handleGenerate}>
              Re-generate
            </TextButton>
          </Footer>
        </>
      ) : null}
    </Section>
  )
}

export default ListingBreakdown
